import type {
  QuotedTweet,
  Tweet,
  TweetEntities,
  TweetParent,
} from "react-tweet/api";

type PartialTweetEntities = Partial<TweetEntities> | undefined;

type ReactTweetParentPayload = Omit<TweetParent, "entities"> & {
  entities?: Partial<TweetEntities>;
};

type ReactQuotedTweetPayload = Omit<QuotedTweet, "entities"> & {
  entities?: Partial<TweetEntities>;
};

export type ReactTweetPayload = Omit<
  Tweet,
  "entities" | "parent" | "quoted_tweet"
> & {
  entities?: Partial<TweetEntities>;
  parent?: ReactTweetParentPayload;
  quoted_tweet?: ReactQuotedTweetPayload;
};

function normalizeTweetEntities(entities: PartialTweetEntities): TweetEntities {
  return {
    hashtags: entities?.hashtags ?? [],
    urls: entities?.urls ?? [],
    user_mentions: entities?.user_mentions ?? [],
    symbols: entities?.symbols ?? [],
    ...(entities?.media ? { media: entities.media } : {}),
  };
}

function hasIterableTweetEntities(entities: PartialTweetEntities): boolean {
  return Boolean(
    entities &&
      Array.isArray(entities.hashtags) &&
      Array.isArray(entities.urls) &&
      Array.isArray(entities.user_mentions) &&
      Array.isArray(entities.symbols)
  );
}

function normalizeTweetParentForReactTweet(
  parent: ReactTweetParentPayload
): TweetParent {
  const { entities, ...rest } = parent;

  return {
    ...rest,
    entities: normalizeTweetEntities(entities),
  };
}

function normalizeQuotedTweetForReactTweet(
  tweet: ReactQuotedTweetPayload
): QuotedTweet {
  const { entities, ...rest } = tweet;

  return {
    ...rest,
    entities: normalizeTweetEntities(entities),
  };
}

export function tweetNeedsReactTweetNormalization(
  tweet: ReactTweetPayload
): boolean {
  return Boolean(
    !hasIterableTweetEntities(tweet.entities) ||
      (tweet.parent && !hasIterableTweetEntities(tweet.parent.entities)) ||
      (tweet.quoted_tweet &&
        !hasIterableTweetEntities(tweet.quoted_tweet.entities))
  );
}

export function normalizeTweetForReactTweet(tweet: ReactTweetPayload): Tweet {
  const { entities, parent, quoted_tweet, ...rest } = tweet;

  return {
    ...rest,
    entities: normalizeTweetEntities(entities),
    ...(parent ? { parent: normalizeTweetParentForReactTweet(parent) } : {}),
    ...(quoted_tweet
      ? { quoted_tweet: normalizeQuotedTweetForReactTweet(quoted_tweet) }
      : {}),
  };
}
